import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Check } from "lucide-react";
import { usePageSEO, SEO } from "@/lib/seo";
import CampaignCounter from "@/components/CampaignCounter";

const useT = () => {
  const { lang } = useI18n();
  return (da, en, es) => ({ da, en, es })[lang] || da;
};

export default function RegisterRun() {
  const { lang } = useI18n();
  const t = useT();
  usePageSEO(SEO.registration.title, SEO.registration.description);
  const [cfg, setCfg] = useState(null);
  const [f, setF] = useState({
    option: "",  // no preselection — user must pick to prevent accidental checkouts
    quantity: 1,
    full_name: "", email: "", phone: "",
    shirt_sizes: [""], comments: "",
    tip_dkk: 0,
    terms_accepted: false, photo_consent: false,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { api.get("/register/config").then(r => setCfg(r.data)).catch(() => {}); }, []);

  const opt = cfg?.options?.[f.option];
  const includesShirt = !!opt?.includes_shirt;
  const required = includesShirt ? f.quantity : 0;
  useEffect(() => {
    setF(x => {
      const arr = x.shirt_sizes.slice(0, required);
      while (arr.length < required) arr.push("");
      return { ...x, shirt_sizes: arr };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.option, f.quantity]);

  const total = (opt?.amount_dkk || 0) * f.quantity + Math.max(0, Number(f.tip_dkk) || 0);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const setSize = (i, v) => setF(x => ({ ...x, shirt_sizes: x.shirt_sizes.map((s, ix) => ix === i ? v : s) }));
  const allSizesChosen = useMemo(() => required === 0 || (f.shirt_sizes.length === required && f.shirt_sizes.every(Boolean)), [f.shirt_sizes, required]);

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!f.option) return setErr(t("Vælg en pakke.", "Please choose an option.", "Elige una opción."));
    if (!f.terms_accepted) return setErr(t("Du skal acceptere vilkårene.", "You must accept the terms.", "Debes aceptar los términos."));
    if (!allSizesChosen) return setErr(t("Vælg størrelse for alle T-shirts.", "Choose a size for every shirt.", "Elige una talla para cada camiseta."));
    setBusy(true);
    try {
      const { data } = await api.post("/register/run", { ...f, origin_url: window.location.origin, language: lang });
      window.location.href = data.checkout_url;
    } catch (ex) {
      setErr(ex.response?.data?.detail || t("Der opstod en fejl.", "Something went wrong.", "Ha ocurrido un error."));
      setBusy(false);
    }
  };

  return (
    <div className="max-editorial container-x py-16 max-w-3xl">
      <div className="label-mono text-terracotta">{t("TILMELDING", "REGISTRATION", "INSCRIPCIÓN")}</div>
      <h1 data-testid="rr-title" className="font-editorial text-4xl md:text-6xl mt-3 tracking-tighter">{t("Tilmeld dig Social Run for Spain", "Register for Social Run for Spain", "Inscríbete en Social Run for Spain")}</h1>
      <p className="mt-6 text-ink/70 text-lg leading-relaxed max-w-2xl">
        {t(
          "Vær med til Social Run for Spain og støt de samfund, der er ramt af skovbrandene i Spanien. Vælg din tilmeldingsmulighed, udfyld dine oplysninger og betal sikkert online.",
          "Join Social Run for Spain and help support the communities affected by the wildfires in Spain. Choose your registration option, complete your details and pay securely online.",
          "Únete a Social Run for Spain y ayuda a las comunidades afectadas por los incendios en España. Elige tu opción de inscripción, completa tus datos y paga de forma segura online."
        )}
      </p>

      <p className="mt-3 text-sm">
        <Link to="/events/social-run-for-spain#programme" data-testid="rr-view-programme" className="text-terracotta underline underline-offset-4 hover:text-terracotta-dark">
          {t("Se dagens program", "View the event programme", "Ver el programa del evento")} →
        </Link>
      </p>

      <p className="mt-2 text-xs text-ink/60 leading-relaxed" data-testid="rr-raffle-note">
        {t(
          "Social Run for Spain afholdes den 6. september. Velgørenhedslotteriets lodtrækning finder sted den 25. oktober i KB Hallen.",
          "Social Run for Spain takes place on 6 September. The charity raffle draw will be held on 25 October at KB Hallen.",
          "Social Run for Spain se celebra el 6 de septiembre. El sorteo de la rifa solidaria tendrá lugar el 25 de octubre en KB Hallen."
        )}
      </p>

      <CampaignCounter className="mt-8" data-testid="rr-campaign-counter" />

      <form onSubmit={submit} className="mt-10 space-y-10" data-testid="rr-form">
        {/* Options */}
        <section>
          <label className="label-mono text-ink/60">{t("Vælg din pakke", "Choose your option", "Elige tu opción")}</label>
          <div className="mt-3 grid gap-3">
            {cfg?.options && Object.entries(cfg.options).map(([k, o]) => {
              const isPackage = k === "package";
              const active = f.option === k;
              return (
                <button key={k} type="button" data-testid={`rr-opt-${k}`}
                        onClick={() => set("option", k)}
                        className={`relative text-left p-5 border rounded-xl transition-colors ${active ? "border-terracotta bg-terracotta/5" : "border-line hover:border-ink"}`}>
                  {isPackage && (
                    <span data-testid="rr-supporter-badge" className="absolute -top-3 right-4 bg-terracotta text-white text-[11px] tracking-wider px-3 py-1 rounded-full">
                      {t("Supporter-pakke", "Supporter package", "Pack de apoyo")}
                    </span>
                  )}
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="font-editorial text-xl">{o[`label_${lang}`] || o.label_en}</div>
                    <div className="font-editorial text-2xl text-terracotta">{o.amount_dkk} DKK</div>
                  </div>
                  <div className="mt-2 text-xs text-ink/60">
                    {k === "participation" && t(
                      "Tilmelding til Social Run for Spain. Denne mulighed inkluderer ikke en T-shirt.",
                      "Registration for Social Run for Spain. This option does not include a T-shirt.",
                      "Inscripción a Social Run for Spain. Esta opción no incluye camiseta."
                    )}
                    {k === "shirt" && t("Inkluderer en solidaritets-T-shirt fra DANAmark Solidaritet, som afhentes på løbedagen.", "Includes a solidarity T-shirt from DANAmark Solidaritet, to be collected at the event.", "Incluye una camiseta solidaria de DANAmark Solidaritet, que se recogerá el día del evento.")}
                    {k === "package" && t("Inkluderer deltagelse i Social Run for Spain og en solidaritets-T-shirt.", "Includes participation in Social Run for Spain and a solidarity T-shirt.", "Incluye la participación en Social Run for Spain y una camiseta solidaria.")}
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Personal */}
        <section className="space-y-5">
          <Field label={t("Fulde navn", "Full name", "Nombre completo")} testid="rr-name" v={f.full_name} on={v => set("full_name", v)} required />
          <Field label={t("E-mail", "Email", "Email")} testid="rr-email" type="email" v={f.email} on={v => set("email", v)} required />
          <Field label={t("Telefonnummer", "Telephone number", "Teléfono")} testid="rr-phone" v={f.phone} on={v => set("phone", v)} required />
          <div>
            <label className="label-mono text-ink/60">{t("Antal", "Number of participants / units", "Número de participantes / unidades")}</label>
            <input data-testid="rr-qty" type="number" min="1" max="20" value={f.quantity}
                   onChange={e => set("quantity", Math.max(1, Number(e.target.value) || 1))}
                   className="mt-2 w-full border border-line focus:border-terracotta outline-none px-4 py-3 bg-transparent" />
          </div>
        </section>

        {/* Shirt sizes */}
        {required > 0 && cfg && (
          <section className="space-y-3" data-testid="rr-sizes-section">
            <label className="label-mono text-ink/60">
              {t("T-shirt størrelser", "Shirt sizes", "Tallas de camiseta")} · {f.shirt_sizes.filter(Boolean).length}/{required}
            </label>
            <div className="grid gap-2">
              {f.shirt_sizes.map((s, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="label-mono text-ink/50 w-24">{t("T-shirt", "Shirt", "Camiseta")} {i + 1}</span>
                  <select data-testid={`rr-size-${i}`} value={s || ""} onChange={e => setSize(i, e.target.value)} required
                          className="flex-1 border border-line focus:border-terracotta outline-none px-3 py-2 bg-transparent text-sm rounded-md">
                    <option value="">— {t("Vælg", "Choose", "Elige")} —</option>
                    {cfg.sizes.map(sz => {
                      const rem = cfg.stock?.[sz]?.remaining ?? 0;
                      const soldOut = rem <= 0;
                      const soldOutLabel = t("Udsolgt", "Sold out", "Agotado");
                      const lowLabel = t("kun", "only", "solo");
                      const suffix = soldOut ? ` — ${soldOutLabel}` : (rem <= 5 ? ` (${lowLabel} ${rem})` : "");
                      return (
                        <option key={sz} value={sz} disabled={soldOut} data-testid={`rr-size-opt-${sz}`}>
                          {sz}{suffix}
                        </option>
                      );
                    })}
                  </select>
                </div>
              ))}
            </div>
            <p className="text-xs text-ink/60">{t(
              "Tjek din størrelse omhyggeligt. T-shirts udleveres på løbedagen og sendes ikke.",
              "Please check your size carefully. T-shirts will be handed out at the event and will not be shipped.",
              "Comprueba bien tu talla. Las camisetas se entregarán el día del evento y no se enviarán por correo."
            )}</p>
          </section>
        )}

        {/* Comments */}
        <div>
          <label className="label-mono text-ink/60">{t("Kommentarer, allergier, særlige behov", "Comments, allergies or special needs", "Comentarios, alergias o necesidades especiales")}</label>
          <textarea data-testid="rr-comments" rows="3" value={f.comments} onChange={e => set("comments", e.target.value)}
                    className="mt-2 w-full border border-line focus:border-terracotta outline-none px-4 py-3 bg-transparent" />
        </div>

        {/* Consents */}
        <div className="space-y-3">
          <label className="flex items-start gap-3 text-sm">
            <input type="checkbox" data-testid="rr-terms" checked={f.terms_accepted}
                   onChange={e => set("terms_accepted", e.target.checked)} required className="mt-1" />
            <span>{t(
              "Jeg accepterer vilkårene, privatlivspolitikken og behandling af mine data for at administrere tilmeldingen.",
              "I accept the terms, the privacy policy and the processing of my data required to manage this registration.",
              "Acepto los términos, la política de privacidad y el tratamiento de mis datos necesarios para gestionar la inscripción."
            )}</span>
          </label>
          <label className="flex items-start gap-3 text-sm">
            <input type="checkbox" data-testid="rr-photo" checked={f.photo_consent}
                   onChange={e => set("photo_consent", e.target.checked)} className="mt-1" />
            <span>{t(
              "Jeg giver DANAmark Solidaritet tilladelse til at bruge fotos og videoer fra arrangementet i sine kommunikationskanaler og sociale medier.",
              "I authorize DANAmark Solidaritet to use photographs and videos from the event in its communication channels and social media.",
              "Autorizo a DANAmark Solidaritet a utilizar fotografías y vídeos del evento en sus canales de comunicación y redes sociales."
            )}</span>
          </label>
        </div>

        {/* Summary */}
        <section className="border border-line rounded-xl p-6 bg-line/10" data-testid="rr-summary">
          <div className="label-mono text-ink/60 mb-3">{t("Oversigt", "Summary", "Resumen")}</div>

          {/* Optional donation */}
          <div className="mb-5 pb-5 border-b border-line/70">
            <div className="label-mono text-ink/60 text-[11px]">{t("Frivilligt bidrag (valgfrit)", "Add a donation (optional)", "Añade una donación (opcional)")}</div>
            <div className="mt-2 flex flex-wrap gap-2 items-center">
              {[0, 25, 50, 100].map(v => (
                <button key={v} type="button" data-testid={`rr-tip-${v}`}
                        onClick={() => set("tip_dkk", v)}
                        className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${Number(f.tip_dkk) === v ? "border-terracotta text-terracotta bg-terracotta/5" : "border-line hover:border-ink"}`}>
                  {v === 0 ? t("Nej tak", "No thanks", "No, gracias") : `+${v} DKK`}
                </button>
              ))}
              <div className="flex items-center gap-1">
                <input type="number" min="0" step="5" value={f.tip_dkk || ""} placeholder={t("Andet beløb", "Custom", "Otro")}
                       data-testid="rr-tip-custom"
                       onChange={e => set("tip_dkk", Math.max(0, Number(e.target.value) || 0))}
                       className="w-24 border border-line focus:border-terracotta outline-none px-2 py-1.5 text-xs bg-transparent rounded" />
                <span className="text-xs text-ink/50">DKK</span>
              </div>
            </div>
            <p className="mt-2 text-[11px] text-ink/50">{t(
              "De indsamlede midler støtter genopretningen af de områder i Spanien, der er ramt af skovbrandene. Indtægter, udgifter og den endelige overførsel offentliggøres på vores transparensside.",
              "The funds raised support the recovery of areas in Spain affected by the wildfires. Income, expenses and the final transfer will be published on our transparency page.",
              "Los fondos recaudados apoyan la recuperación de las zonas afectadas por los incendios en España. Los ingresos, los gastos y la transferencia final se publicarán en nuestra página de transparencia."
            )}</p>
          </div>

          <div className="grid grid-cols-2 gap-y-1 text-sm">
            <div className="text-ink/60">{t("Navn", "Name", "Nombre")}</div><div>{f.full_name || "—"}</div>
            <div className="text-ink/60">{t("Pakke", "Option", "Opción")}</div><div>{opt?.[`label_${lang}`] || opt?.label_en || t("Ikke valgt", "Not selected", "No seleccionada")}</div>
            <div className="text-ink/60">{t("Deltagere / enheder", "Participants / units", "Participantes / unidades")}</div><div>{f.quantity}</div>
            <div className="text-ink/60">{t("T-shirts", "T-shirts", "Camisetas")}</div><div>{required === 0 ? t("Ingen T-shirt inkluderet", "No T-shirt included", "No incluye camiseta") : required}</div>
            {required > 0 && <><div className="text-ink/60">{t("Størrelser", "Sizes", "Tallas")}</div><div>{f.shirt_sizes.filter(Boolean).join(", ") || "—"}</div></>}
            {Number(f.tip_dkk) > 0 && <><div className="text-ink/60">{t("Frivilligt bidrag", "Donation", "Donación")}</div><div data-testid="rr-summary-tip">+{Number(f.tip_dkk)} DKK</div></>}
            <div className="text-ink/60">{t("Levering", "Delivery", "Entrega")}</div><div>{t("Afhentes på løbedagen", "Collection at the event", "Recogida el día del evento")}</div>
            <div className="text-ink/60 pt-2 border-t border-line mt-2">{t("Total", "Total", "Total")}</div>
            <div className="pt-2 border-t border-line mt-2 font-editorial text-2xl text-terracotta" data-testid="rr-total">{total} DKK</div>
          </div>
        </section>

        {err && <div className="text-sm text-red-600" data-testid="rr-error">{err}</div>}

        <button type="submit" disabled={busy} data-testid="rr-submit"
                className="w-full bg-terracotta hover:bg-terracotta-dark disabled:opacity-60 text-white px-8 py-4 text-base tracking-wide rounded-full">
          {busy ? "…" : t("Tilmeld og betal", "Register and pay", "Inscribirse y pagar")}
        </button>

        <div className="border-t border-line pt-8">
          <div className="font-editorial text-2xl mb-2">{t("Afhentning af T-shirt", "T-shirt collection", "Recogida de la camiseta")}</div>
          <p className="text-ink/70 text-sm">{t(
            "Alle T-shirts skal afhentes på løbedagen. De sendes ikke. Vi sender flere praktiske oplysninger om afhentningssted og tidsplan før arrangementet.",
            "All T-shirts must be collected at the event. They will not be shipped. More practical information about the collection point and schedule will be sent before the event.",
            "Todas las camisetas se recogen el día del evento. No se envían por correo. Enviaremos más información práctica sobre el punto de recogida y el horario antes del evento."
          )}</p>
        </div>
      </form>
    </div>
  );
}

function Field({ label, v, on, type = "text", testid, required }) {
  return (
    <div>
      <label className="label-mono text-ink/60">{label}</label>
      <input data-testid={testid} type={type} required={required} value={v}
             onChange={e => on(e.target.value)}
             className="mt-2 w-full border border-line focus:border-terracotta outline-none px-4 py-3 bg-transparent" />
    </div>
  );
}

export function RegisterRunThankYou() {
  const { lang } = useI18n();
  const t = useT();
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [status, setStatus] = useState("loading");
  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let n = 0;
    const poll = async () => {
      n++;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") { setStatus("paid"); return; }
      } catch (e) {}
      if (n < 20) setTimeout(poll, 2000); else setStatus("timeout");
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="max-editorial container-x py-24 text-center max-w-2xl">
      {status === "paid" ? (
        <>
          <div className="mx-auto w-14 h-14 rounded-full bg-terracotta/10 text-terracotta flex items-center justify-center">
            <Check size={28} />
          </div>
          <h1 data-testid="rr-thanks-title" className="font-editorial text-4xl md:text-5xl mt-6 tracking-tighter">
            {t("Tak fordi du er med til Social Run for Spain!", "Thank you for joining Social Run for Spain!", "¡Gracias por unirte a Social Run for Spain!")}
          </h1>
          <div className="mt-6 text-ink/75 text-lg leading-relaxed space-y-4 max-w-xl mx-auto">
            <p>{t(
              "Din tilmelding og betaling er gennemført. Du modtager en bekræftelse på mail.",
              "Your registration and payment have been successfully completed. You will receive a confirmation by email.",
              "Tu inscripción y el pago se han completado correctamente. Recibirás una confirmación por email."
            )}</p>
            <p>{t(
              "Hvis din bestilling inkluderer en solidaritets-T-shirt, kan du hente den på løbedagen. Gem venligst din bekræftelses-mail og vis den ved afhentning.",
              "If your order includes a solidarity T-shirt, you can collect it at the event. Please keep your confirmation email and show it when collecting your T-shirt.",
              "Si tu compra incluye una camiseta solidaria, podrás recogerla el día del evento. Conserva el email de confirmación y muéstralo al recoger la camiseta."
            )}</p>
            <p className="font-editorial text-2xl text-terracotta">{t(
              "Sammen viser vi, at solidaritet ikke kender grænser.",
              "Together, we show that solidarity knows no borders.",
              "Juntos demostramos que la solidaridad no conoce fronteras."
            )}</p>
          </div>
          <Link to="/" className="mt-10 inline-block border border-ink px-6 py-3 text-sm tracking-wide hover:bg-ink hover:text-white transition-colors rounded-full">
            {t("Tilbage til forsiden", "Back to home", "Volver al inicio")}
          </Link>
        </>
      ) : status === "loading" ? (
        <p className="text-ink/60">…</p>
      ) : (
        <p className="text-red-600">{t("Betalingen kunne ikke bekræftes. Kontakt os venligst.", "Payment could not be confirmed. Please contact us.", "No se ha podido confirmar el pago. Contáctanos, por favor.")}</p>
      )}
    </div>
  );
}
